import React from "react";
import { Card, CardHeader, CardBody, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { threatCard, threatCardHeader, threatCardBody, threatIconChip } from "./cardStyles";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

// Orange gradient palette for MITRE tactics
const MITRE_COLORS = [
  '#FF5B00',  // Primary orange
  '#ff8c61',  // Light orange
  '#e85a28',  // Dark orange
  '#ffb399',  // Lighter orange
  '#d14d1f',  // Darker orange
  '#ff9d7a',  // Mid-light orange
  '#c94219',  // Deep orange
  '#ffac8a',  // Soft orange
];

const MitreAttackTechniques = ({ data, loading }) => {
  if (loading) {
    return (
      <Card 
        className="h-100"
        style={{ 
          border: '1px solid rgba(255, 107, 53, 0.2)', 
          backgroundColor: 'transparent',
          borderRadius: '8px'
        }}
      >
        <CardHeader className="bg-light">
          <h5 className="card-title mb-0">
            <i className="ri-shield-keyhole-line me-2 text-primary"></i>
            MITRE ATT&CK Tactics
          </h5>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  const tactics = data || [];
  
  // Count occurrences
  const tacticCounts = {};
  tactics.forEach(tactic => {
    tacticCounts[tactic] = (tacticCounts[tactic] || 0) + 1;
  });

  const categories = Object.keys(tacticCounts).slice(0, 8);
  const values = categories.map(cat => tacticCounts[cat]);
  
  const totalDetections = values.reduce((sum, val) => sum + val, 0);

  const options = { 
    chart: {
      type: "pie",
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif"
    },
    labels: categories,
    colors: MITRE_COLORS,
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
      style: {
        fontSize: "11px",
        fontWeight: "600",
        colors: ['#fff']
      },
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        opacity: 0.45
      }
    },
    legend: {
      position: "bottom",
      fontSize: "11px",
      markers: {
        width: 10,
        height: 10,
        radius: 2
      },
      labels: {
        colors: ['#858d98']
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4
      }
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `${val} detection${val !== 1 ? 's' : ''}`
      },
      style: {
        fontSize: '12px'
      }
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.1
        }
      }
    }
  };

  const series = values;

  return (
      <Card 
        className="h-100"
        style={{ 
          ...threatCard
        }}
      >
        <CardHeader style={threatCardHeader}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <div style={threatIconChip()}>
                <i className="ri-shield-keyhole-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
              </div>
              <h5 className="card-title mb-0">MITRE ATT&CK Tactics</h5>
            </div>
          {categories.length > 0 && (
            <span 
              className="badge" 
              style={{ 
                backgroundColor: 'rgba(255, 107, 53, 0.1)', 
                color: ORANGE_COLORS.primary,
                border: `1px solid ${ORANGE_COLORS.primary}`,
                fontSize: '11px'
              }}
            >
              {categories.length} tactics
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody style={threatCardBody}>
        {categories.length > 0 ? (
          <>
            <ReactApexChart options={options} series={series} type="pie" height={280} />
            
            {/* Total Detections Summary */}
            <div className="text-center mt-3 pt-3 border-top" style={{ borderColor: 'rgba(255, 107, 53, 0.2)' }}>
              <div className="d-inline-flex align-items-center px-3 py-2 rounded" style={{ backgroundColor: 'rgba(255, 107, 53, 0.08)' }}>
                <div style={threatIconChip()}>
                  <i className="ri-shield-check-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
                </div>
                <div className="text-start">
                  <div className="text-muted" style={{ fontSize: '11px' }}>Total Detections</div>
                  <div className="fw-bold" style={{ color: ORANGE_COLORS.primary, fontSize: '16px' }}>
                    {totalDetections}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <div className="mx-auto mb-3" style={threatIconChip()}>
              <i className="ri-shield-keyhole-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <p className="text-muted mb-0">No MITRE tactics detected</p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default MitreAttackTechniques;
